import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const Home = () => {
  const [activeModal, setActiveModal] = useState(null);

  const closeModal = () => setActiveModal(null);

  return (
    <>
      <main>

        {/* HERO */}
        <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-background">
          <div className="absolute inset-0 bg-halftone z-0"></div>

          <div className="container mx-auto px-4 z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* LEFT */}
            <div className="space-y-6">
              <div className="inline-block bg-accent/20 text-accent-foreground px-4 py-2 rounded-full font-bold text-sm tracking-wide border border-accent/50 transform -rotate-2">
                PROMOTING YOUTH LITERACY
              </div>

              <h1 className="text-5xl md:text-7xl font-display leading-none uppercase">
                Making<br />
                Literacy<br />
                <span className="text-primary">Fun</span>
              </h1>

              <p className="text-xl text-muted-foreground max-w-lg leading-relaxed">
                We fight illiteracy in Tennessee by putting comics and manga into the hands of youth. Join our mission to make reading accessible and fun.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <a href="/donate">
                  <button className="text-lg px-8 py-6 font-bold bg-primary text-white rounded-lg hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                    SUPPORT OUR CAUSE
                  </button>
                </a>

                <a href="#programs">
                  <button className="text-lg px-8 py-6 font-bold border-2 border-input hover:bg-accent/10 rounded-lg transition-colors">
                    SEE OUR WORK
                  </button>
                </a>
              </div>
            </div>

            {/* RIGHT */}
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-secondary rounded-3xl transform rotate-3 translate-x-4 translate-y-4 -z-10"></div>
              <div className="absolute inset-0 bg-accent rounded-3xl transform -rotate-2 -translate-x-2 -translate-y-2 -z-20"></div>

              <img
                src="https://acetn.org/images/library-reading.png"
                alt="Youth reading manga"
                className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3] border-4 border-background"
              />

              <a href="/digital-library">
                <div className="absolute bottom-4 bg-background p-4 rounded-xl shadow-xl border-2 border-foreground max-w-[200px]">
                  <p className="font-display text-xl text-primary text-center leading-none">
                    FREE MANGA LIBRARY!
                  </p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section id="stats" className="py-24 bg-foreground text-background relative overflow-hidden">

          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-background to-transparent z-10"></div>

          <div className="container mx-auto px-4 z-20 relative">

            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-display mb-6 text-primary">
                THE CHALLENGE IN TENNESSEE
              </h2>
              <p className="text-xl text-muted-foreground">
                Illiteracy is a silent crisis affecting our youth's future.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

              <img
                src="https://acetn.org/images/tennessee_map_with_books_data_visualization.png"
                alt="map"
                className="rounded-2xl shadow-2xl border border-white/10 w-full"
              />

              <div className="grid gap-6">

                {/* CARD 1 */}
                <div
                  onClick={() => setActiveModal(1)}
                  className="bg-white/5 border border-white/10 p-6 rounded-lg cursor-pointer hover:bg-white/10 transition"
                >
                  <div className="flex gap-6 items-center">
                    <span className="text-6xl text-accent font-display">13%</span>
                    <p className="text-gray-300">of adults in Tennessee lack basic prose literacy skills.</p>
                  </div>
                </div>

                {/* CARD 2 */}
                <div
                  onClick={() => setActiveModal(2)}
                  className="bg-white/5 border border-white/10 p-6 rounded-lg cursor-pointer hover:bg-white/10 transition"
                >
                  <div className="flex gap-6 items-center">
                    <span className="text-6xl text-secondary font-display">60%</span>
                    <p className="text-gray-300">of low-income students have no books at home.</p>
                  </div>
                </div>

                {/* CARD 3 */}
                <div
                  onClick={() => setActiveModal(3)}
                  className="bg-white/5 border border-white/10 p-6 rounded-lg cursor-pointer hover:bg-white/10 transition"
                >
                  <div className="flex gap-6 items-center">
                    <span className="text-6xl text-primary font-display">3rd</span>
                    <p className="text-gray-300">Grade reading level is a key predictor of graduation.</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* MODALS */}
        {activeModal && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <div
              className="bg-white text-black p-8 rounded-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >

              <button onClick={closeModal} className="mb-4 font-bold">
                ✕ Close
              </button>

              {activeModal === 1 && (
                <>
                  <h3 className="text-xl font-bold mb-2">Adult Literacy Gap</h3>
                  <p>
                    When 13% of adults lack basic literacy skills, it impacts the entire state's economy and health.
                  </p>
                </>
              )}

              {activeModal === 2 && (
                <>
                  <h3 className="text-xl font-bold mb-2">Book Deserts</h3>
                  <p>
                    Many low-income communities lack access to books, limiting early childhood development.
                  </p>
                </>
              )}

              {activeModal === 3 && (
                <>
                  <h3 className="text-xl font-bold mb-2">The Critical Milestone</h3>
                  <p>
                    Students not reading by 3rd grade are far more likely to drop out later.
                  </p>
                </>
              )}

            </div>
          </div>
        )}

        {/* PROGRAMS */}
        <section id="programs" className="py-24 bg-background relative">
        <div className="container mx-auto px-4">

            <div className="text-center mb-16">
            <span className="text-primary font-bold tracking-wider uppercase mb-2 block">
                Our Solution
            </span>
            <h2 className="text-4xl md:text-5xl font-display mb-6">
                HOW WE TAKE ACTION
            </h2>
            </div>

            {/* 3 CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">

            <div className="bg-white border rounded-2xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold mb-3">Manga Distribution</h3>
                <p>We provide free comics and manga to spark reading.</p>
            </div>

            <div className="bg-white border rounded-2xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold mb-3">Traveling Library</h3>
                <p>Our mobile library brings books directly to underserved communities.</p>
            </div>

            <div className="bg-white border rounded-2xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold mb-3">Anime Conventions</h3>
                <p>Events that combine pop culture with literacy education.</p>
            </div>
            </div>

            {/* FEATURED TRAVELING LIBRARY SECTION (THIS WAS MISSING) */}
            <div className="rounded-3xl overflow-hidden bg-primary text-primary-foreground shadow-2xl">

            <div className="grid grid-cols-1 md:grid-cols-2">

                <div className="p-12 flex flex-col justify-center">
                <h3 className="text-3xl md:text-4xl font-display mb-6">
                    THE TRAVELING LIBRARY
                </h3>

                <p className="text-primary-foreground/90 text-lg mb-8 leading-relaxed">
                    Our flagship program hits the road to bring reading material to where kids are.
                    We visit schools, parks, and community events with curated manga collections.
                </p>

                <ul className="space-y-4">
                    <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-white rounded-full" />
                    Free to access for all students
                    </li>
                    <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-white rounded-full" />
                    Curated for age-appropriateness
                    </li>
                    <li className="flex items-center gap-3">
                    <span className="w-2 h-2 bg-white rounded-full" />
                    Safe, inclusive community space
                    </li>
                </ul>
                </div>

                <div className="relative h-64 md:h-auto">
                <img
                    src="https://acetn.org/images/traveling-library.png"
                    alt="Traveling Library"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-transparent" />
                </div>

            </div>
            </div>

        </div>
        </section>

        {/* MISSION */}
        <section id="mission" className="py-24 bg-muted/30 text-center">
          <div className="container mx-auto max-w-4xl">

            <h2 className="text-3xl md:text-4xl font-display mb-8">
              WHY MANGA?
            </h2>

            <p className="text-xl text-muted-foreground leading-relaxed">
              Traditional books can be intimidating for struggling readers. Manga breaks text into visual context, improving comprehension and engagement.
            </p>

          </div>
        </section>

      </main>

      <Footer />
    </>
  );
};

export default Home;